import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Form,
  Input,
  Select,
  DatePicker,
  Spin,
  Tag,
} from "antd";
import { counties } from "../../../counties";
import { languages } from "../../../languages";
import { nationalities } from "../../../nationalities";
import {
  fetchProfile,
  updateProfile,
  handleNextStep,
} from "../../../features/profile/profileSlice";
import { showToastError, showToastSuccess } from "../../../utils";
import toast from "react-hot-toast";
import dayjs from "dayjs";

const { TextArea } = Input;

const countyOptions = counties.map((item) => ({
  label: item?.name,
  value: item?.name,
}));

const languageOptions = languages.map((item) => ({
  label: item?.name,
  value: item?.name,
}));

const nationalityOptions = nationalities.map((item) => ({
  label: item,
  value: item,
}));

const genderOptions = [
  { label: "Male", value: "Male" },
  { label: "Female", value: "Female" },
  { label: "Other", value: "Other" },
];

const maritalOptions = [
  { label: "Single", value: "Single" },
  { label: "Married", value: "Married" },
  { label: "Divorced", value: "Divorced" },
  { label: "Widowed", value: "Widowed" },
];

const ethnicityOptions = [
  "Samburu", "Turkana", "Swahili", "Sakuye", "Maasai", "Kamba",
  "Kisii", "Somalis", "Nilotic", "Meru", "Bantu", "Orma",
  "Boorana", "Kikuyu", "Luhya", "Luo", "Kalenjin",
].map((e) => ({ label: e, value: e }));

const majorReligions = [
  "Christianity", "Islam", "Hinduism", "Buddhism",
  "Judaism", "Sikhism", "Jainism", "Taoism",
].map((r) => ({ label: r, value: r }));

const jobSeekingOptions = [
  { label: "Letter of application", value: "Letter of application" },
  { label: "CV", value: "CV" },
  { label: "Profile", value: "Profile" },
];

const tagRender = (props) => {
  const { label, closable, onClose } = props;
  return (
    <Tag
      onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
      closable={closable}
      onClose={onClose}
      style={{ marginRight: 3 }}
    >
      {label}
    </Tag>
  );
};

function closeModal() {
  toast.dismiss();
}

export default function PersonalStep() {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const formRef = useRef(null);

  const { profileLoading, profileData } = useSelector((state) => state.profile);

  const today = new Date();
  const dd = String(today.getDate()).padStart(2, "0");
  const mm = String(today.getMonth() + 1).padStart(2, "0");
  const yyyy = today.getFullYear() - 18;
  const minDate = `${yyyy}-${mm}-${dd}`;

  useEffect(() => {
    dispatch(fetchProfile());
  }, [dispatch]);

  useEffect(() => {
    if (profileData) {
      form.setFieldsValue({
        middleName: profileData.middleName,
        gender: profileData.gender,
        county: profileData.county,
        nationality: profileData.nationality,
        ethnicity: profileData.ethnicity,
        maritalStatus: profileData.maritalStatus,
        languages: profileData.languages,
        religion: profileData.religion,
        dateOfBirth: profileData.dateOfBirth ? dayjs(profileData.dateOfBirth) : null,
        jobCareerTitle: profileData.jobCareerTitle,
        bio: profileData.bio,
        jobSeekingMethods: profileData.jobSeekingMethods,
        currentJob: profileData.currentJob,
        hasDisability: profileData.hasDisability ?? false,
      });
    }
  }, [profileData, form]);

  const onFinish = async (values) => {
    const payload = {
      middleName: values.middleName,
      gender: values.gender,
      county: values.county,
      nationality: values.nationality,
      ethnicity: values.ethnicity,
      maritalStatus: values.maritalStatus,
      languages: values.languages || [],
      religion: values.religion,
      dateOfBirth: values.dateOfBirth ? dayjs(values.dateOfBirth).format("YYYY-MM-DD") : null,
      jobCareerTitle: values.jobCareerTitle,
      bio: values.bio,
      jobSeekingMethods: values.jobSeekingMethods || [],
      currentJob: values.currentJob,
      hasDisability: values.hasDisability ?? false,
    };

    const res = await dispatch(updateProfile(payload));
    if (res?.payload?.success) {
      showToastSuccess("Profile saved successfully", closeModal);
      dispatch(handleNextStep());
    } else {
      showToastError(res?.payload?.message || "Failed to save profile", closeModal);
    }
  };

  if (profileLoading && !profileData) {
    return (
      <div className="flex items-center justify-center mt-20">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="mt-10">
      <h3 className="edit-step-head">Personal Info</h3>
      <p className="edit-step-desc mt-2">
        Make sure you give accurate details so that future employers can reach you.
      </p>

      <Form
        layout="vertical"
        ref={formRef}
        name="personal-info"
        onFinish={onFinish}
        style={{ maxWidth: "100%", marginTop: 20 }}
        form={form}
      >
        {/* Read-only fields from registration */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6">
          <Form.Item label="First Name">
            <Input
              readOnly
              value={profileData?.firstName}
              className="rounded-[8px] h-[44px] w-full border border-gray-300 bg-gray-50"
            />
          </Form.Item>
          <Form.Item name="middleName" label="Middle Name">
            <Input className="rounded-[8px] h-[44px] w-full border border-black" />
          </Form.Item>
          <Form.Item label="Last Name">
            <Input
              readOnly
              value={profileData?.lastName}
              className="rounded-[8px] h-[44px] w-full border border-gray-300 bg-gray-50"
            />
          </Form.Item>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
          <Form.Item label="National ID">
            <Input
              readOnly
              value={profileData?.nationalId}
              className="rounded-[8px] h-[44px] w-full border border-gray-300 bg-gray-50"
            />
          </Form.Item>
          <Form.Item label="Email">
            <Input
              readOnly
              value={profileData?.email}
              className="rounded-[8px] h-[44px] w-full border border-gray-300 bg-gray-50"
            />
          </Form.Item>
          <Form.Item label="Phone Number">
            <Input
              readOnly
              value={profileData?.phoneNumber}
              className="rounded-[8px] h-[44px] w-full border border-gray-300 bg-gray-50"
            />
          </Form.Item>

          <Form.Item name="gender" label="Gender">
            <Select
              style={{ width: "100%", height: "44px" }}
              options={genderOptions}
              placeholder="Select gender"
            />
          </Form.Item>

          <Form.Item name="county" label="County">
            <Select
              style={{ width: "100%", height: "44px" }}
              options={countyOptions}
              placeholder="Select county"
              showSearch
              filterOption={(input, option) =>
                (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
              }
            />
          </Form.Item>

          <Form.Item name="nationality" label="Nationality">
            <Select
              style={{ width: "100%", height: "44px" }}
              options={nationalityOptions}
              placeholder="Select nationality"
              showSearch
              filterOption={(input, option) =>
                (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
              }
            />
          </Form.Item>

          <Form.Item name="ethnicity" label="Ethnicity">
            <Select
              style={{ width: "100%", height: "44px" }}
              options={ethnicityOptions}
              placeholder="Select ethnicity"
            />
          </Form.Item>

          <Form.Item name="maritalStatus" label="Marital Status">
            <Select
              style={{ width: "100%", height: "44px" }}
              options={maritalOptions}
              placeholder="Select marital status"
            />
          </Form.Item>

          <Form.Item name="languages" label="Languages">
            <Select
              mode="multiple"
              tagRender={tagRender}
              style={{ width: "100%", minHeight: "44px" }}
              options={languageOptions}
              placeholder="Select languages"
            />
          </Form.Item>

          <Form.Item name="religion" label="Religion">
            <Select
              style={{ width: "100%", height: "44px" }}
              options={majorReligions}
              placeholder="Select religion"
            />
          </Form.Item>

          <Form.Item name="dateOfBirth" label="Date of Birth">
            <DatePicker
              style={{ width: "100%", height: "44px" }}
              format="YYYY-MM-DD"
              placeholder="Select date of birth"
              disabledDate={(current) => current && current > dayjs(minDate)}
            />
          </Form.Item>

          <Form.Item name="jobSeekingMethods" label="Job Seeking Methods">
            <Select
              mode="multiple"
              style={{ width: "100%", minHeight: "44px" }}
              options={jobSeekingOptions}
              placeholder="Select methods"
            />
          </Form.Item>

          <Form.Item name="currentJob" label="Current Job">
            <Input
              className="rounded-[8px] h-[44px] w-full border border-black"
              placeholder="e.g. Software Engineer at XYZ"
            />
          </Form.Item>

          <Form.Item name="hasDisability" label="Person with Disability?">
            <Select
              style={{ width: "100%", height: "44px" }}
              options={[
                { label: "Yes", value: true },
                { label: "No", value: false },
              ]}
              placeholder="Select"
            />
          </Form.Item>
        </div>

        <Form.Item name="jobCareerTitle" label="Job/Career Title">
          <Input
            className="rounded-[8px] h-[44px] w-full border border-black"
            placeholder="e.g. Software Engineer"
          />
        </Form.Item>

        <div>
          <p className="mb-1 font-medium text-[15px]">Bio</p>
          <p className="label-info">
            Tell future employers a little about yourself.
          </p>
          <div className="w-full mt-3">
            <Form.Item name="bio" className="w-full">
              <TextArea rows={4} placeholder="Write a short bio..." />
            </Form.Item>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row items-center justify-center mt-10 mb-14 w-full">
          <button
            className="bg-darkBlue rounded-[35px] text-white w-full sm:w-[300px] p-3"
            type="submit"
            disabled={profileLoading}
          >
            {profileLoading ? <Spin size="small" /> : "Save & Next"}
          </button>
        </div>
      </Form>
    </div>
  );
}
